import AbstractDispatcher from 'frontools/abstract/AbstractDispatcher';
import GameAbstract from '../core/GameAbstract';
import Template from 'frontools/data/Template';
import { autobind } from 'frontools/decorators/Autobind';
import { Level, ILevelConfig } from './Level';
import { Assets } from '../core/Assets';
import NumberUtils from 'frontools/utils/NumberUtils';
import { GameMode, GameManager } from '../core/GameManager';

declare var dataLayer           : any;

export class Crocroc extends GameAbstract {

    private _currentLevel                   : number                                  = 0;
    private _levels                         : Level[]                                 = [];
    private _levelWrapper                   : PIXI.Container                          = new PIXI.Container;
    private _levelConfig                    : Object[]                                = [
        {
            character: 'carrot',
            particleColor: 0xcc4f11,
            faceOffsetX: 0,
            faceOffsetY: 110,
            gridSizeH: 2,
            gridSizeV: 4
        },
        {
            character: 'pineapple',
            particleColor: 0xe8bb3a,
            faceOffsetX: -15,
            faceOffsetY: 200,
            gridSizeH: 3,
            gridSizeV: 8
        },
        {
            character: 'log',
            particleColor: 0x79583c,
            faceOffsetX: 0,
            faceOffsetY: 160,
            posOffsetY: 20,
            gridSizeH: 4,
            gridSizeV: 8,
            ieShadow: false
        },
        {
            character: 'rock',
            particleColor: 0x706c64,
            faceOffsetX: -10,
            faceOffsetY: 90,
            posOffsetY: 30,
            gridSizeH: 10,
            gridSizeV: 13,
            ieShadow: false
        },
    ];


    constructor() {
        
        super({
            time: 20,
            results: [
                '0-84',
                '85-114',
                '115-9999'
            ],
            assets: [
                { name: 'face', url: 'sprite/face/face.json', init: false },
                { name: 'explosion', url: 'sprite/explosion/explosion.json', init: false },
                { name: 'dentier', url: 'sprite/dentier/dentier.json' },
                { name: 'board', url: 'sprite/board.png' },
                { name: 'carrot', url: 'sprite/carrot.png' },
                { name: 'pineapple', url: 'sprite/pineapple.png' },
                { name: 'log', url: 'sprite/log.png' },
                { name: 'rock', url: 'sprite/rock.png' },
                { name: 'bite', url: 'sprite/bite.png' },

                { name: 'end', url: 'audio/end.mp3' },
                { name: 'level', url: 'audio/level.mp3' },
                { name: 'crunch', url: 'audio/crunch.mp3' },
            ]
            
        });

    };


    /*
     * @core override
     * _onPreloadComplete - Called when the preload has been completed
     * 
     */
    protected _onPreloadComplete() {
        super._onPreloadComplete();
    };


    /*
     * @core override
     * _prepare - Prepare the game components and then starts the countdown
     * 
     */    
    protected _prepare() {
        this._stage.addChild(Assets.sprites['board']);
        this._stage.addChild(Assets.sprites['dentier']);
        
        // Prepare denture sprite
        Assets.sprites['dentier'].animationSpeed = .2;
        Assets.sprites['dentier'].loop = false;
        if (this._resolution === 1) {
            Assets.sprites['dentier'].scale = new PIXI.Point(.5, .5);
        }
        
        this._generateLevel(this._currentLevel, false);


        // Trigger components positioning
        // Note: This will initialise core game components and start the countdown
        super._prepare(); 
    };


    private _generateLevel(index: number, active: boolean = true) {
        let level = new Level(this._levelConfig[index] as ILevelConfig);

        // custom tracking for Crocroc
        if (this._currentLevel > 0) {
            dataLayer.push({URI: '/jeu-educatifs/' + this._config.name +'/jeu/etape'+ (this._currentLevel + 1), event: 'virtualPageview'});
        }
        
        if (active === false) level.disable();
        
        this._levels.push(level);
        this._resize();
        TweenMax.from(level.container, .5, {x: "+=400", alpha: 1, ease: Expo.easeOut});

        // Events
        level.addListener(Level.COMPLETE, this._onLevelComplete);
        level.addListener(Level.HIT, this._onHit);
    };


    /*
     * _onLevelComplete - Starts the game
     * 
     */
    @autobind
    private _onLevelComplete(e) {
        this._gameScore.add(this._gameTimer.timeLeft);

        this._levels[this._currentLevel].removeListener(Level.HIT, this._onHit);
        e.target.removeListener(Level.COMPLETE, this._onLevelComplete);

        if (this._currentLevel >= this._levelConfig.length -1) {
            this._gameTimer.stop(); // This trigger the end game
        } else {
            Assets.sounds['level'].play();
            this._currentLevel++;
            this._generateLevel(this._currentLevel);
        }
    };


    /*
     * _onLevelComplete - Starts the game
     * 
     */
    @autobind
    private _onHit(e) {
        this._gameScore.add(1);
        
        Assets.sounds['crunch'].rate(NumberUtils.random(1, 1.5, false)).play();
        //Assets.sounds['crunch'].play();
        Assets.sprites['dentier'].gotoAndPlay(1);
    };


    /*
     * @core override
     * _start - Starts the game
     * 
     */
    protected _start() {
        super._start();
        //Assets.sounds['bgm'].play();
        this._levels[0].enable();
    };
    

    /*
     * @core override
     * _onTimerComplete - on game timer complete handler
     * @param {event} e - Event
     * 
     */
    protected _onTimerComplete(e) {
        super._onTimerComplete(e);
        Assets.sounds['end'].play();
    };


    protected _end() {
        super._end();

        //Assets.sounds['bgm'].fade(1, 0, 1000);
    }


    /*
     * @core override
     * _resize - Resize handler
     * @param {event} e - Event
     * 
     */
    protected _resize(e?: Event) {
        super._resize(e);

        if (this._gameMode === GameMode.PLAYING || typeof e === 'undefined') {

            Assets.sprites['board'].x = this._screenWidth / 2 - Assets.sprites['board'].width / 2;
            Assets.sprites['board'].y = this._screenHeight - Assets.sprites['board'].height - 20;
            if (this._context.isIE()) {
                Assets.sprites['board'].visible = false;
            }
        
            let level = this._levels[this._currentLevel] as Level;
            level.container.x = this._screenWidth / 2 - level.container.width / 2;
            level.container.y = Assets.sprites['board'].y - level.container.height + 20 + (level.config.posOffsetY || 0);
            

            Assets.sprites['dentier'].x = Assets.sprites['board'].x + Assets.sprites['board'].width - Assets.sprites['dentier'].width / 2;
            Assets.sprites['dentier'].y = Assets.sprites['board'].y + Assets.sprites['board'].height - Assets.sprites['dentier'].height + 15;
        }
    };

}